import { ArrowLeft, ImageUser, Link01, Mail01, Trash01, Upload01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ActionTooltip from 'src/components/ActionTooltip';
import ButtonCmp from 'src/components/ButtonCmp';
import InputText from 'src/components/InputText/InputText';
import PageSectionLayout from 'src/components/PageSectionLayout';
import Header from 'src/components/PageSectionLayout/Header/Header';
import PhoneInput from 'src/components/PhoneInput/PhoneInput';
import SelectBox from 'src/components/SelectBox/SelectBox';
import { currencyList } from 'src/constants/common';
import { ROUTES } from 'src/constants/routes';
import { setCurrentUser, setIsUpdateProfile } from 'src/redux/auth.slice';
import { RootState } from 'src/redux/store';
import { updateSetting } from 'src/services/SettingService';
import { getShortName, onError } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

import defaultImage from '../../assets/img/default-image.jpg';
import EditIconwithBorder from '../../assets/img/EditIconwithBorder.svg';
import CropperModal from '../../components/CropperModal/CropperModal';

import SharedLinkDocumentsSection from './SharedLinkDocumentsSection';

const settingsSections = [
    {
        id: 'profile',
        title: 'Profile Settings',
        icon: ImageUser,
    },
    // cp-meeting-changes F3: documents on the customer's shared tracking links
    {
        id: 'shared-link',
        title: '', // rendered through t() in the sidebar
        icon: Link01,
    },
];

const AccountSettings = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const initValidForm = {
        firstName: true,
        lastName: true,
        title: true,
        image: true,
        phone: true,
        phone_valid: true,
        image_size_valid: true,
        image_type_valid: true,
    };

    const currentUser = useSelector((state: RootState) => state.auth.currentUser);
    const [isFormSubmitted, setIsFormSubmitted] = useState<any>(false);

    const getInitFormData = () => ({
        firstName: currentUser?.firstName || '',
        lastName: currentUser?.lastName || '',
        email: currentUser?.email || '',
        phone: currentUser?.phone || '',
        title: currentUser?.title || '',
        image: '',
        defaultCurrency: currentUser?.defaultCurrency?.toLowerCase() || 'cad',
    });

    const [formData, setFormData] = useState<any>(getInitFormData);
    const [validForm, setValidForm] = useState(initValidForm);
    const [isPhoneNumberValue, setIsPhoneNumberValue] = useState(false);
    const [displayImage, setDisplayImage] = useState<any>(null);
    const [showCropper, setShowCropper] = useState(false);
    const [cropperRawUrl, setCropperRawUrl] = useState<string>('');
    const [updateProfile, setIsUpdated] = useState(false);
    const { t } = useTranslation();
    const [activeSection, setActiveSection] = useState('profile');
    const initialFormDataRef = useRef<any>(null);
    const initialImageRef = useRef<any>(null);

    useEffect(() => {
        const defaultProfileImage = currentUser?.image ? `${currentUser?.imageUrl}${currentUser?.image}` : null;
        const init = getInitFormData();
        setDisplayImage(defaultProfileImage);
        setFormData(init);
        initialFormDataRef.current = init;
        initialImageRef.current = defaultProfileImage;
    }, [currentUser]);

    const isDirty = useMemo(() => {
        const init = initialFormDataRef.current;
        if (!init) return false;
        const fieldsChanged =
            formData.firstName !== init.firstName ||
            formData.lastName !== init.lastName ||
            formData.title !== init.title ||
            formData.phone !== init.phone ||
            formData.defaultCurrency?.toLowerCase() !== init.defaultCurrency?.toLowerCase();
        const imageChanged = displayImage !== initialImageRef.current;

        return fieldsChanged || imageChanged;
    }, [formData, displayImage]);

    const ALLOWED_TYPES = ['image/png', 'image/jpeg'];

    const onDrop = useCallback((acceptedFiles: File[]) => {
        const file = acceptedFiles[0];
        if (!file) return;

        if (!ALLOWED_TYPES.includes(file.type)) {
            setValidForm((prev: any) => ({ ...prev, image_type_valid: false }));

            return;
        }

        const reader = new FileReader();

        reader.onload = (e) => {
            const img = new Image();

            img.onload = () => {
                setValidForm((prev: any) => ({ ...prev, image_type_valid: true, image: true }));
                setCropperRawUrl(e.target!.result as string);
                setShowCropper(true);
            };

            img.onerror = () => {
                WalToast.error('File corrupted');
                setValidForm((prev: any) => ({ ...prev, image_type_valid: false }));
            };

            img.src = e.target!.result as string;
        };

        reader.readAsDataURL(file);
    }, []);

    const {
        getRootProps,
        getInputProps,
        open: openFilePicker,
    } = useDropzone({
        onDrop,
        maxFiles: 1,
        multiple: false,
        accept: {
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
        },
    });

    const isFormValid = () => {
        let valid = true;
        const validFormData = { ...validForm };

        if (!formData.firstName?.trim()) {
            validFormData.firstName = false;
            valid = false;
        }

        if (!formData.lastName?.trim()) {
            validFormData.lastName = false;
            valid = false;
        }

        if (!formData.title?.trim()) {
            validFormData.title = false;
            valid = false;
        }

        if (!formData.phone) {
            validFormData.phone = false;
            valid = false;
        }

        setValidForm({ ...validForm, ...validFormData });

        return valid;
    };

    const handleInputChange = (event: any) => {
        const { name, value } = event.target;
        setFormData((prev: any) => ({ ...prev, [name]: value }));

        if (name === 'firstName') setValidForm((prev: any) => ({ ...prev, firstName: value.trim() !== '' }));
        if (name === 'lastName') setValidForm((prev: any) => ({ ...prev, lastName: value.trim() !== '' }));
        if (name === 'title') setValidForm((prev: any) => ({ ...prev, title: value.trim() !== '' }));
    };

    const handleSubmit = async (event: any) => {
        event.preventDefault();
        setIsFormSubmitted(true);

        if (!isFormValid()) return;

        setIsFormSubmitted(false);
        setIsUpdated(true);

        const appendFormData: any = new FormData();
        appendFormData.append('firstName', formData.firstName.trim());
        appendFormData.append('lastName', formData.lastName.trim());
        appendFormData.append('title', formData.title);
        appendFormData.append('phone', formData.phone);
        appendFormData.append('defaultCurrency', formData.defaultCurrency?.toLowerCase());

        const isNewImage = displayImage && displayImage !== initialImageRef.current && displayImage.startsWith('data:');
        // User chose "Remove photo" on an existing photo and didn't upload a replacement
        const isImageRemoved = !displayImage && !!initialImageRef.current;

        if (isNewImage) {
            const base64Response = await fetch(displayImage);
            const blob = await base64Response.blob();
            appendFormData.append('profileImage', blob, 'profile.jpg');
        } else if (isImageRemoved) {
            appendFormData.append('removeProfileImage', 'true');
        }

        updateSetting(appendFormData)
            .then((response: any) => {
                const updatedUser = response?.data ?? response;
                dispatch(
                    setCurrentUser({
                        ...currentUser,
                        firstName: formData.firstName.trim(),
                        lastName: formData.lastName.trim(),
                        title: formData.title,
                        phone: formData.phone,
                        defaultCurrency: formData.defaultCurrency,
                        ...(updatedUser?.image && { image: updatedUser.image }),
                        ...(updatedUser?.imageUrl && { imageUrl: updatedUser.imageUrl }),
                        // The truthy spreads above skip null, so clear explicitly on removal —
                        // otherwise the header avatar keeps showing the deleted photo.
                        ...(isImageRemoved && { image: null, imageUrl: null }),
                    })
                );
                initialFormDataRef.current = { ...formData };
                initialImageRef.current = displayImage;
                dispatch(setIsUpdateProfile(true));
                WalToast.success('Settings updated');
            })
            .finally(() => setIsUpdated(false))
            .catch(() => WalToast.error('Profile update failed'));
    };

    const handleNumberChange = (country: any, number: string, code: string) => {
        const fullPhone = number?.trim() ? `${code}${number}` : '';
        setFormData((prev: any) => ({ ...prev, phone: fullPhone }));
        setValidForm((prev: any) => ({ ...prev, phone: number?.trim() !== '' }));
    };

    const SettingsSidebar = () => (
        <ul className="stepper-sidebar-wrap space-y-1">
            {settingsSections.map((section) => {
                const isActive = activeSection === section.id;
                const Icon = section.icon;

                return (
                    <li key={section.id} className={`vertical-secondary-tab-item ${isActive && 'vertical-secondary-tab-item-active'}`} onClick={() => setActiveSection(section.id)}>
                        <div className="vertical-secondary-tab-item-icon-wrap">
                            <Icon className="vertical-secondary-tab-item-icon" />
                        </div>
                        <h3 className="vertical-secondary-tab-item-title">{section.id === 'shared-link' ? t('SharedLinkSettings.Section_Title') : section.title}</h3>
                    </li>
                );
            })}
        </ul>
    );

    return (
        <>
            <PageSectionLayout
                headerClassName="stepper-container"
                contentClassName="stepper-container overflow-y-auto"
                header={
                    <Header
                        title={
                            <div className="flex items-center gap-x-2.5">
                                <ButtonCmp className="btn_secondary_black" size="square-sm" onClick={() => navigate(ROUTES.HOME, { replace: true })}>
                                    <ArrowLeft className="w-4 h-4" />
                                </ButtonCmp>
                                Settings
                            </div>
                        }
                        isShowNotificationBox
                    />
                }
            >
                <div className="stepper-box-wrap">
                    <SettingsSidebar />

                    {activeSection === 'shared-link' ? (
                        <div className="flex-1 overflow-auto scrollbar-hide bg-inherit stepper-content-wrap">
                            <SharedLinkDocumentsSection />
                        </div>
                    ) : (
                        <div className="flex-1 overflow-auto scrollbar-hide bg-inherit stepper-content-wrap">
                            <div className="pb-3 border-b border-utilityGray200 sticky bg-inherit top-0 z-[3] stepper-content-header">
                                <div className="flex justify-between items-center gap-x-2">
                                    <div className="flex flex-col">
                                        <h6 className="text-grayLight900 text-base font-semibold">Profile Settings</h6>
                                        <p className="text-grayLight600 text-xs leading-[1.5] font-normal">Manage your personal information and preferences.</p>
                                    </div>
                                    <div className="flex items-center gap-x-2 justify-end">
                                        <ButtonCmp
                                            type="button"
                                            className="btn_secondary_black"
                                            disabled={!isDirty}
                                            onClick={() => {
                                                setFormData(getInitFormData());
                                                setDisplayImage(initialImageRef.current);
                                                setValidForm(initValidForm);
                                                setIsFormSubmitted(false);
                                            }}
                                        >
                                            Cancel
                                        </ButtonCmp>
                                        <ButtonCmp type="button" className="btn-outline-primary" onClick={handleSubmit} loading={updateProfile} disabled={updateProfile || !isDirty}>
                                            Save
                                        </ButtonCmp>
                                    </div>
                                </div>
                            </div>

                            <form className="[&>*]:py-5 pb-6" onSubmit={handleSubmit}>
                                {/* Profile Photo */}
                                <div className="stepper-content-container border-b border-utilityGray200">
                                    <label className="stepper-lable-wrap flex">
                                        <div className="stepper-label-common">Profile Photo</div>
                                    </label>
                                    <div className="w-full flex-1">
                                        <div className="flex items-center gap-4 justify-between">
                                            <div className="relative w-[48px] h-[48px] shrink-0">
                                                {displayImage ? (
                                                    <img className="table-profile-icn-circle-xs rounded-full h-[48px]   w-[48px]" src={displayImage} alt="profile" onError={onError} />
                                                ) : formData.firstName ? (
                                                    <div className="table-profile-customer-circle-xxl w-full h-full text-lg">{getShortName(`${formData.firstName} ${formData.lastName}`)}</div>
                                                ) : (
                                                    <img className="table-profile-icn-circle-xs rounded-full h-[48px]   w-[48px]" src={defaultImage} alt="default" />
                                                )}
                                                {/* Pencil + photo menu only when an actual photo is displayed (not
                                                initials/default) — with no photo, the Upload button beside the avatar
                                                is the single entry point. Removal is applied on Save, same as the
                                                upload flow; Cancel restores the photo. */}
                                                {displayImage && (
                                                    <ActionTooltip
                                                        message={
                                                            <ul className="action-tooltip-select min-w-[150px]">
                                                                <li className="action-tooltip-select-item" onClick={openFilePicker}>
                                                                    <Upload01 className="action-tooltip-select-item-icon" />
                                                                    <p className="action-tooltip-select-item-title">Upload photo</p>
                                                                </li>
                                                                <li
                                                                    className="action-tooltip-select-item"
                                                                    onClick={() => {
                                                                        setDisplayImage(null);
                                                                        setFormData((prev: any) => ({ ...prev, image: '' }));
                                                                    }}
                                                                >
                                                                    <Trash01 className="action-tooltip-select-item-icon text-fgErrorPrimary" />
                                                                    <p className="action-tooltip-select-item-title text-fgErrorPrimary">Remove photo</p>
                                                                </li>
                                                            </ul>
                                                        }
                                                        type="light"
                                                        tooltipPosition="center"
                                                        parentClassName="action-tooltip-select-wrap"
                                                        actionTooltipCustomClass="action-tooltip-without-arrow"
                                                        // ActionTooltip's wrapper div is the tippy anchor. With only an
                                                        // absolutely-positioned pencil inside, it has zero size and the menu
                                                        // opens off to the side — so stretch it over the whole 48px avatar:
                                                        // the menu centers under the photo and the photo becomes clickable.
                                                        childrenClassName="!absolute inset-0"
                                                    >
                                                        <div className="cursor-pointer absolute -bottom-1 -right-1 w-[22px] h-[22px] flex items-center justify-center">
                                                            <img src={EditIconwithBorder} alt="edit" className="h-[20px] w-[20px]" />
                                                        </div>
                                                    </ActionTooltip>
                                                )}
                                            </div>

                                            <div {...getRootProps()} className="cursor-pointer">
                                                <input {...getInputProps()} />
                                                <ButtonCmp type="button" size="md" className="btn_secondary_black pointer-events-none" icon={<Upload01 className="w-4 h-4" />}>
                                                    Upload
                                                </ButtonCmp>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Name */}
                                <div className="stepper-content-container border-b border-utilityGray200">
                                    <label className="stepper-lable-wrap flex">
                                        <div className="stepper-label-common">Name</div>
                                    </label>
                                    <div className="w-full flex-1 gap-3 flex flex-row">
                                        <InputText
                                            inputName="firstName"
                                            placeholder="First Name"
                                            parentClassName="flex-1"
                                            maxLength={50}
                                            value={formData.firstName}
                                            onChangeFunc={handleInputChange}
                                            errorText={isFormSubmitted && !validForm.firstName ? true : false}
                                        />
                                        <InputText
                                            inputName="lastName"
                                            placeholder="Last Name"
                                            parentClassName="flex-1"
                                            maxLength={50}
                                            value={formData.lastName}
                                            onChangeFunc={handleInputChange}
                                            errorText={isFormSubmitted && !validForm.lastName ? true : false}
                                        />
                                    </div>
                                </div>

                                {/* Email — read-only */}
                                <div className="stepper-content-container border-b border-utilityGray200">
                                    <label className="stepper-lable-wrap flex">
                                        <div className="stepper-label-common">Email Address</div>
                                    </label>
                                    <div className="w-full flex-1">
                                        <InputText
                                            inputType="email"
                                            inputName="email"
                                            placeholder="Email"
                                            value={formData.email}
                                            iconLeft={<Mail01 className="input-currency-sign" />}
                                            onChangeFunc={() => {}}
                                            className="shadow pl-7 disabled:bg-gray50 disabled:text-gray500 text-black/90 cursor-not-allowed"
                                            parentClassName="w-full"
                                            disabled={true}
                                        />
                                    </div>
                                </div>

                                {/* Title */}
                                <div className="stepper-content-container border-b border-utilityGray200">
                                    <label className="stepper-lable-wrap flex">
                                        <div className="stepper-label-common">Title</div>
                                    </label>
                                    <div className="w-full flex-1">
                                        <InputText
                                            inputName="title"
                                            placeholder="Enter title"
                                            parentClassName="flex-1 w-full"
                                            value={formData.title}
                                            onChangeFunc={handleInputChange}
                                            errorText={isFormSubmitted && !validForm.title ? 'Title is required' : null}
                                        />
                                    </div>
                                </div>

                                {/* Phone Number */}
                                <div className="stepper-content-container border-b border-utilityGray200">
                                    <label className="stepper-lable-wrap flex">
                                        <div className="stepper-label-common">Phone number</div>
                                    </label>
                                    <div className="w-full flex-1">
                                        <PhoneInput
                                            phoneInputWrapClassName="w-full"
                                            phoneInputParentClassName="w-full"
                                            phoneInputClassName="w-full"
                                            value={formData?.phone ? `+${formData.phone}` : ''}
                                            onNumberChange={handleNumberChange}
                                            isButtonClicked={isFormSubmitted}
                                            isPhoneNumberValue={isPhoneNumberValue}
                                            setIsPhoneNumberValue={setIsPhoneNumberValue}
                                            errors={isFormSubmitted && !validForm.phone ? 'Phone number is required.' : ''}
                                            name="phone"
                                        />
                                    </div>
                                </div>

                                {/* Default Currency */}
                                <div className="stepper-content-container border-b border-utilityGray200">
                                    <label className="stepper-lable-wrap flex">
                                        <div className="stepper-label-common">Default Currency</div>
                                    </label>
                                    <div className="w-full flex-1">
                                        <SelectBox
                                            options={currencyList}
                                            placeholder="Select currency"
                                            parentClassName="w-full"
                                            onChangeFunc={(selectedOption: any) => setFormData((prev: any) => ({ ...prev, defaultCurrency: selectedOption?.value }))}
                                            value={currencyList.find((c) => c.value?.toLowerCase() === formData?.defaultCurrency?.toLowerCase()) ?? null}
                                            name="defaultCurrency"
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </PageSectionLayout>

            {showCropper && (
                <CropperModal
                    imageUrl={cropperRawUrl}
                    imageUpload={(croppedImage: string) => {
                        setDisplayImage(croppedImage);
                        setFormData((prev: any) => ({ ...prev, image: croppedImage }));
                        setShowCropper(false);
                    }}
                    setUpload={setShowCropper}
                    setCropData={() => {}}
                    isCropType={false}
                    defaultCropType="Square"
                    modalTitle="Crop Profile Photo"
                    modalDesc="Adjust the crop area to set your profile photo."
                />
            )}
        </>
    );
};

export default AccountSettings;
